import {
  contractDuration,
  contractDurationDates,
  getProductsPrices,
  getTermString,
} from './getProductsPrices'
import { getDiscountPercentage } from './getDiscountPercentage'
import { filterLegacyProductsByState } from './legacySubscriptionUtils'
import { isValid, parseISO, parse, format } from 'date-fns'
import { find, some, sumBy } from 'lodash'

import {
  isSmallBusinessProduct,
  isAnnualProduct,
  isLegacyProduct,
  isValidCreditUnit,
  isPayUpFrontProduct,
  isUnlimitedProduct,
} from './determineProductType'

export const productsIds = {
  premiumSupport: 'premium_support_v1',
  reservedCredits: 'credits1_reserved_v1',
  overage: 'credits1_overage_v1',
  unlimited: 'credits_unlimited_v1',
}

/**
 * Subscription available statuses
 * - active: Subscription is active
 * - past_due: Subscription is past due on payment but considered as an active subscription
 * - canceled: Subscription is canceled, expired, or has a failed payment.  If status is 'canceled' it might mean org had previous subscription and card is stored in Stripe
 * - pending: Subscription has an aws provider and is pending user to accept from their aws account
 * - requested: Subscription has an aws provider and is requested by the user. This is a state when user requested a private offer from us and we need to create it
 */
const STATUS = {
  ACTIVE: 'active',
  PAST_DUE: 'past_due', // past due state is considered as an active subscription
  CANCELED: 'canceled',
  PENDING: 'pending',
  REQUESTED: 'requested',
}

/**
 * Billing providers
 * - aws: AWS Marketplace can be private or public offer. Public offer can only be purchased by the user from the AWS Marketplace, private offer can be requested from dashboard and user needs to accept it from their AWS account
 * - stripe: Stripe is used for self service subscriptions
 * - manual: Manual subscription is a custom agreement with the user created by admin
 */
const PROVIDER = {
  AWS: 'aws',
  STRIPE: 'stripe',
  MANUAL: 'manual',
}

/**
 * Get subscription status object
 * @param {Object} subscription - Subscription object
 * @returns Status object
 */
export const getSubscriptionStatus = (subscription) => ({
  active: subscription?.status === STATUS.ACTIVE || subscription?.status === STATUS.PAST_DUE, // past due state is considered as an active subscription
  pastDue: subscription?.status === STATUS.PAST_DUE,
  pending: subscription?.status === STATUS.PENDING,
  requested: subscription?.status === STATUS.REQUESTED,
  canceled: subscription?.status === STATUS.CANCELED,
  failed: subscription?.status === STATUS.FAILED,
})

/**
 * Get subscription provider object
 * @param {Object} subscription - Subscription object
 * @returns Provider object
 */
export const getSubscriptionProvider = (subscription) => ({
  isAws: subscription?.provider === PROVIDER.AWS,
  isStripe: subscription?.provider === PROVIDER.STRIPE,
  isManual: subscription?.provider === PROVIDER.MANUAL,
})

/**
 * Check if the update payment method button should be disabled
 * @param {Object } subscription - Subscription object
 * @returns {boolean} - Whether the update payment method button should be disabled
 */
export const isUpdatePaymentMethodDisabled = (subscription) =>
  !hasSubscriptionRecord(subscription) || isSubscriptionViewOnly(subscription)

/**
 * Check if the subscription is view only
 * @param {Object} subscription - Subscription object
 * @returns {boolean} - Whether the subscription is view only
 */
export const isSubscriptionViewOnly = (subscription) =>
  getSubscriptionStatus(subscription)?.active &&
  (getSubscriptionProvider(subscription)?.isAws ||
    getSubscriptionProvider(subscription)?.isManual ||
    getSubscriptionType(subscription)?.isPayUpFront ||
    getSubscriptionType(subscription)?.isUnlimited)

/**
 * Check if the subscription exists by checking if it has a provider property
 * @param {Object} subscription - Subscription object
 * @returns {boolean} - Whether the subscription has a provider record
 */
export const hasSubscriptionRecord = (subscription) => !!subscription?.provider

/**
 * Check if the subscription is free
 * Subscription is free if it doesn't have a record or it's status is canceled
 * @param {Object} subscription - Subscription object
 * @returns {boolean} - Whether the subscription is free
 */
export const isFreeSubscription = (subscription) =>
  !hasSubscriptionRecord(subscription) || subscription.status === STATUS.CANCELED

/**
 * Check if the subscription has at least one legacy product
 * @param {Object} subscription - Subscription object
 * @returns {boolean} - Whether the subscription is legacy
 */
export const isLegacySubscription = (subscription) => subscription?.products?.some(isLegacyProduct)

/**
 * Get an object of reserved credits for 15, 50, and 300 unit sizes
 * @param {Object} subscription - Subscription object
 * @returns {Object} - Legacy subscription bundles of 15, 50, and 300 unit sizes
 */
export const getLegacySubscriptionBundles = (subscription) => {
  /**
   * Filter products to have only products with 15, 50, or 300 unit sizes
   */
  const reservedCreditsProducts = subscription?.pricing?.products.filter(isValidCreditUnit)
  /**
   * Get reserved credits object for 15, 50, and 300 unit sizes
   */
  const reservedCredits = reservedCreditsProducts?.reduce((acc, product) => {
    if (!product.unitSize) return acc

    return {
      ...acc,
      [product.unitSize]: product.units || 0,
    }
  }, {})
  return reservedCredits
}

/**
 * Check if the product is pay upfront. Pay upfront products have the term and interval equal
 * and term is annual (more than 1)
 * @param {Object} recurring - Recurring object with term and interval properties
 * @returns {boolean} - Whether the subscription is pay upfront
 */
export const isPayUpFrontTerm = (recurring) =>
  recurring?.term > 1 && recurring?.term === recurring?.interval

/**
 * This function basically returns an enriched state details based on current subscription
 * This is used to determine the current total credits, total price, discount percentage, etc.
 * @param {Object} state - Current state object
 * @param {Object} subscription - Subscription object
 * @returns {Object} - Enriched subscription details object
 */
export const getStateSubscriptionDetails = (state, subscription) => {
  if (isLegacySubscription(subscription)) {
    /**
     * Filter active products based on state
     */
    const filteredProducts = filterLegacyProductsByState(state)

    let products = []

    products = filteredProducts?.map((product) => {
      return {
        ...product,
        units: state.legacyReservedCredits[product.unitSize],
      }
    })

    // Check if current subscription is annual
    const hasAnnualProduct = some(products, isAnnualProduct)
    const total = sumBy(products, (n) => n.units * n.unitPrice * (n.interval || 1)) || 0
    const totalCredits = sumBy(products, (n) => n.totalCredits || n?.units * n?.unitSize) || 0

    /**
     * We want to default term to 12 months because legacy subscriptions with reserved credits can only resubscribe for 12 months
     */
    let term = hasAnnualProduct ? 12 : 1

    /**
     * Take any product from the pricing array to get the interval
     */
    const product = products?.[0]
    let interval = product?.recurring?.interval

    let termString = getTermString(interval)
    const recurring = {
      term,
      interval,
    }
    return {
      products,
      total,
      totalCredits,
      recurring,
      termString,
      totalDiscountPercentage: null,
    }
  } else {
    const { term, interval } = state.recurring || {}

    const { startDate, endDate } = contractDurationDates(term)
    const units = state.reservedCredits || 0
    const products = []
    if (state.isReserved) {
      products.push({
        id: 'credits1_reserved_v1',
        units,
        term,
        interval,
        startDate,
        endDate,
        autoRenew: true,
        unitSize: 1,
      })
    }
    if (state.isPayAsYouGo) {
      products.push({
        id: 'credits1_overage_v1',
        startDate,
        endDate,
        autoRenew: true,
        unitSize: 1,
        units: 0,
      })
    }
    if (state.isPremiumSupport && units >= 3000) {
      products.push({
        id: 'premium_support_v1',
        startDate,
        endDate,
        autoRenew: true,
        interval,
        unitSize: 1,
        units,
      })
    }
    const productsPrices = getProductsPrices(products, state.isSmallBusiness)
    return productsPrices
  }
}

/**
 * This function basically returns an enriched subscription details based on current subscription
 * Adds more enriched data that the backend doesn't include by default. This working with the subscription easier
 * @param {Object} subscription - Subscription object
 * @returns {Object} - Enriched subscription details object
 */
export const getSubscriptionDetails = (subscription) => {
  if (isLegacySubscription(subscription)) {
    const total = subscription?.pricing?.total
    const totalCredits = sumBy(subscription?.pricing?.products, (n) => n.units * n.unitSize)
    const isAnnual = getSubscriptionType(subscription)?.isAnnual
    let term = isAnnual ? 12 : 1
    const interval = subscription?.pricing?.products?.[0]?.interval || 1
    const termString = getTermString(interval)
    const recurring = {
      term,
      interval,
    }

    return {
      total,
      totalCredits,
      recurring,
      termString,
      totalDiscountPercentage: null,
    }
  } else {
    const overageProduct = find(subscription?.products, (p) => p.id === productsIds?.overage)
    const supportProduct = getProduct(subscription, productsIds.premiumSupport)

    const reservedProduct = getProduct(subscription, productsIds.reservedCredits)

    const unlimitedProduct = getProduct(subscription, productsIds.unlimited)
    const startDate =
      reservedProduct?.startDate ||
      overageProduct?.startDate ||
      unlimitedProduct?.startDate ||
      supportProduct?.startDate
    const endDate =
      reservedProduct?.endDate ||
      overageProduct?.endDate ||
      unlimitedProduct?.endDate ||
      supportProduct?.endDate

    const interval =
      reservedProduct?.interval ||
      overageProduct?.interval ||
      unlimitedProduct?.interval ||
      supportProduct?.interval ||
      0

    const units =
      reservedProduct?.units ||
      overageProduct?.units ||
      unlimitedProduct?.units ||
      supportProduct?.units

    const unitPrice =
      reservedProduct?.unitPrice ||
      overageProduct?.unitPrice ||
      unlimitedProduct?.unitPrice ||
      supportProduct?.unitPrice

    const term = contractDuration(endDate, startDate)

    let totalCredits = units

    if (unlimitedProduct) {
      totalCredits = 'unlimited'
    }
    if (reservedProduct) {
      totalCredits = reservedProduct.units
    }
    const recurring = {
      term,
      interval,
    }
    const total = subscription?.pricing?.total
    const termString = getTermString(interval)
    const totalDiscountPercentage = getDiscountPercentage(4, unitPrice)
    return {
      total,
      totalCredits,
      recurring,
      termString,
      totalDiscountPercentage,
    }
  }
}

/**
 * Get product object from subscription based on product ID
 * @param {Object} subscription = Subscription object
 * @param {String} productId - Product ID
 * @returns {Object} - Product object
 */
export const getProduct = (subscription, productId) =>
  subscription?.products?.find(
    (p) => (p.id === productId || p.id.includes(productId)) && p.units > 0
  )

/**
 * Get subscription type object based on the subscription
 * @param {Object} subscription - Subscription object
 * @returns {Object} - Subscription type object
 */
export const getSubscriptionType = (subscription) => {
  if (isLegacySubscription(subscription)) {
    const overageProduct = subscription?.products?.find((p) => p.id === productsIds?.overage)
    /**
     * Filter products to have only products with 15, 50, or 300 unit sizes
     */
    const reservedCreditsProducts = subscription?.pricing?.products.filter(isValidCreditUnit)

    // Check if user toggled the overage switch by checking if at least one overage product in the plan
    const isPayAsYouGo = !!overageProduct

    // Check if user toggled the reserved switch by checking if at least one product has units (added credits)
    const isReserved = some(reservedCreditsProducts, (n) => !!n?.units)

    // Check if user toggled the pay upfront switch by checking if at least one pay upfront product in the plan
    const isPayUpFront = some(subscription?.products, isPayUpFrontProduct)

    // Check if current subscription has at least one small business product
    const isSmallBusiness = some(subscription?.products, isSmallBusinessProduct)

    // Check if subscription has an unlimited product
    const isUnlimited = some(subscription?.products, isUnlimitedProduct)

    // Check if current subscription is annual
    const isAnnual = some(subscription?.pricing?.products, isAnnualProduct)

    const subscriptionType = {
      isPayAsYouGo,
      isReserved,
      isPayUpFront,
      isSmallBusiness,
      isUnlimited,
      isAnnual,
      isPremiumSupport: false, // no support product in legacy subscriptions
    }
    return subscriptionType
  } else {
    /**
     * Filter out overage product from the subscription products
     * To be able to tell if pay as you go enabled
     */
    const overageProduct = find(subscription?.products, (p) => p.id === productsIds?.overage)
    /**
     * If subscription is not legacy, we need to enrich the subscription differently
     */

    const supportProduct = getProduct(subscription, productsIds.premiumSupport)

    const reservedProduct = getProduct(subscription, productsIds.reservedCredits)

    const unlimitedProduct = getProduct(subscription, productsIds.unlimited)

    // Check if user toggled the overage switch by checking if at least one overage product in the plan
    const isPayAsYouGo = !!overageProduct

    // Check if user toggled the premium support switch by checking if at least one support product in the plan
    const hasPremiumSupport = !!supportProduct

    // Check if user toggled the reserved switch by checking if at least one product has units (added credits)
    const isReserved = !!reservedProduct?.units

    // Check if current subscription has at least one small business product
    const isSmallBusiness = subscription?.isSmallBusiness

    // Check if subscription has an unlimited product
    const isUnlimited = !!unlimitedProduct
    const subscriptionDetails = getSubscriptionDetails(subscription)
    // Check if current subscription is annual
    const isAnnual = subscriptionDetails?.recurring?.term > 1

    const isPayUpFront =
      isAnnual && subscriptionDetails?.recurring.term === subscriptionDetails?.recurring.interval

    const subscriptionType = {
      isPayAsYouGo,
      isReserved,
      isPayUpFront,
      isSmallBusiness,
      isUnlimited,
      isAnnual,
      isPremiumSupport: hasPremiumSupport,
    }
    return subscriptionType
  }
}

/**
 * Compares subscription states and determines if there are changes
 * @param {object} state - Current UI state of the subscription
 * @param {object} subscription - Current subscription data
 * @returns {boolean} - Whether the subscription has been updated
 */
export const hasSubscriptionChanged = (state, subscription) => {
  const stateSubscription = getStateSubscriptionDetails(state, subscription)
  if (!state || !subscription || !stateSubscription || isSubscriptionViewOnly(subscription))
    return false

  // Compare each property for changes
  const comparisons = {
    creditsChanged:
      stateSubscription?.totalCredits !== getSubscriptionDetails(subscription)?.totalCredits,
    subscriptionTermChanged:
      state?.recurring?.term !== getSubscriptionDetails(subscription)?.recurring?.term,
    qualificationChanged: state?.isQualified !== subscription?.isQualified,
    smallBusinessChanged:
      state?.isSmallBusiness !== getSubscriptionType(subscription)?.isSmallBusiness,
    isPayUpFrontChanged:
      isPayUpFrontTerm(state.recurring) !== getSubscriptionType(subscription)?.isPayUpFront,
    isPayAsYouGoChanged: state?.isPayAsYouGo !== getSubscriptionType(subscription)?.isPayAsYouGo,
    isReservedChanged: state?.isReserved !== getSubscriptionType(subscription)?.isReserved,
    isPremiumSupportChanged:
      state?.isPremiumSupport !== getSubscriptionType(subscription)?.isPremiumSupport,
  }

  // Check if any comparison is true
  return Object.values(comparisons).some((value) => value)
}

/**
 * Format a subscription date to a US date format
 */
export const formatSubscriptionDate = (date) => {
  if (!date) return '' // Ensure date is not null or undefined

  let parsedDate

  // Try to parse ISO format first
  if (typeof date === 'string' && !isNaN(Date.parse(date))) {
    parsedDate = parseISO(date)
  } else if (typeof date === 'string') {
    // Attempt to parse other string formats
    parsedDate = parse(date, 'yyyy-MM-dd', new Date())
  } else {
    // Assume it's a Date object
    parsedDate = new Date(date)
  }

  // Validate the parsed date
  if (!isValid(parsedDate)) return ''

  // Format the valid date
  return format(parsedDate, 'MMM dd, yyyy')
}
